import { useEffect, useState, type FormEvent } from "react";
import { useParams, Link } from "react-router-dom";
import { PageHeader, Panel } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { FunctionsError } from "../lib/functions-client";
import { getAuction, placeBid, closeAuction, auctionPrice, timeLeft, type Auction as A, type AuctionBid } from "../lib/auctions";

export function Auction() {
  const { id } = useParams();
  const [auction, setAuction] = useState<A | null>(null);
  const [bids, setBids] = useState<AuctionBid[]>([]);
  const [isSeller, setIsSeller] = useState(false);
  const [minNext, setMinNext] = useState(0);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  async function refresh() {
    if (!id) return;
    try {
      const d = await getAuction(id);
      setAuction(d.auction);
      setBids(d.bids);
      setIsSeller(d.is_seller);
      setMinNext(d.min_next);
    } catch {
      setNotFound(true);
    }
  }
  useEffect(() => { void refresh(); /* eslint-disable-next-line */ }, [id]);

  async function bid(e: FormEvent) {
    e.preventDefault();
    if (!id || !amount.trim()) return;
    setBusy(true);
    setNote(null);
    try {
      await placeBid(id, amount);
      setAmount("");
      await refresh();
    } catch (err) {
      setNote(err instanceof FunctionsError ? err.message : "Could not place your bid.");
    } finally {
      setBusy(false);
    }
  }

  async function close() {
    if (!id) return;
    setBusy(true);
    try {
      await closeAuction(id);
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  if (notFound) return <div className="mx-auto max-w-2xl p-6"><p className="text-sm text-danger">Auction not found.</p><Link to="/auctions" className="text-accent-strong">← Auctions</Link></div>;
  if (!auction) return <p className="p-6 font-mono text-sm text-muted">Loading…</p>;

  const ended = auction.status !== "active" || new Date(auction.ends_at).getTime() <= Date.now();

  return (
    <div className="mx-auto grid w-full max-w-2xl gap-6">
      <PageHeader title={auction.title} subtitle={`Sold by ${auction.seller_name ?? "a member"}`} />
      <Link to="/auctions" className="font-mono text-[11px] text-accent-strong">← All auctions</Link>

      <Panel>
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-widest text-muted">Current bid</p>
            <p className="font-display text-3xl font-black text-accent-strong">{auctionPrice(auction.current_price_cents)}</p>
            <p className="font-mono text-[11px] text-muted">{auction.bid_count} bid{auction.bid_count === 1 ? "" : "s"}</p>
          </div>
          <p className="font-mono text-sm font-bold">{ended ? "Ended" : timeLeft(auction.ends_at)}</p>
        </div>
        {auction.description && <p className="whitespace-pre-wrap border-t border-line pt-3 text-sm text-muted">{auction.description}</p>}

        {!ended && !isSeller && (
          <form className="flex gap-2 border-t border-line pt-3" onSubmit={bid}>
            <input className={inputClass} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Bid ${auctionPrice(minNext)} or more`} />
            <Button type="submit" disabled={busy || !amount.trim()}>Place bid</Button>
          </form>
        )}
        {!ended && isSeller && <Button variant="ghost" disabled={busy} onClick={close}>End auction now</Button>}
        {ended && auction.winner_account_id && <p className="font-mono text-sm text-verified">Won at {auctionPrice(auction.current_price_cents)}</p>}
        {ended && !auction.winner_account_id && <p className="font-mono text-sm text-muted">Ended with no winning bid.</p>}
        {note && <p className="font-mono text-sm text-danger">{note}</p>}
      </Panel>

      {bids.length > 0 && (
        <Panel title="Bid history">
          <ul className="grid gap-1">
            {bids.map((b, i) => (
              <li key={i} className="flex items-center justify-between font-mono text-sm">
                <span className="text-muted">{b.bidder_name ?? "Bidder"}</span>
                <span className="font-bold">{auctionPrice(b.amount_cents)}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}
